import { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { useBulkUpdateTasks } from '../../hooks/useTasks';
import { useLabels } from '../../hooks/useLabels';
import { usePeople } from '../../hooks/usePeople';
import { useTaskStatuses } from '../../hooks/useTaskStatuses';
import { PRIORITY_OPTIONS } from './taskConstants';
import { toDueISO } from './linear/dueDate';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';
import { useConfirm } from '../ui/ConfirmProvider';
import type { TaskPriority } from '../../lib/tasksApi';

interface BulkActionsBarProps {
  selectedIds: string[];
  onClear: () => void;
}

/**
 * Floating toolbar shown when one or more tasks are selected (FR-TASK #7).
 * Supports bulk status, priority, assignee, due date, label add, and delete.
 * The action dropdowns are stateless triggers: value stays empty and picking
 * an option fires the bulk mutation immediately.
 */
export function BulkActionsBar({ selectedIds, onClear }: BulkActionsBarProps) {
  const bulk = useBulkUpdateTasks();
  const { data: labels } = useLabels();
  const { data: people } = usePeople({ active: 'true' });
  const { statuses } = useTaskStatuses();
  const [labelId, setLabelId] = useState('');
  const confirm = useConfirm();

  if (selectedIds.length === 0) return null;

  const assignable = (people ?? []).filter((p) => p.userId);

  function run(action: Parameters<typeof bulk.mutate>[0]['action'], value?: unknown) {
    bulk.mutate({ ids: selectedIds, action, value }, { onSuccess: onClear });
  }

  return (
    <div className="fixed inset-x-0 bottom-4 z-30 mx-auto flex w-full max-w-3xl flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-3 shadow-lg sm:px-4">
      <span className="text-sm font-medium text-gray-700">{selectedIds.length} selectate</span>

      <Select
        label="Setează status"
        hideLabel
        size="sm"
        value=""
        onChange={(v) => {
          if (v) run('status', v);
        }}
        placeholder="Setează status…"
        options={statuses.map((s) => ({ value: s.id, label: s.name, color: s.color }))}
        className="w-40"
      />

      <Select
        label="Setează prioritate"
        hideLabel
        size="sm"
        value=""
        onChange={(v) => {
          if (v) run('priority', v as TaskPriority);
        }}
        placeholder="Setează prioritate…"
        options={PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        className="w-44"
      />

      <Select
        label="Setează responsabil"
        hideLabel
        size="sm"
        value=""
        onChange={(v) => {
          if (v) run('assignee', v === '__unassign' ? null : v);
        }}
        placeholder="Setează responsabil…"
        options={[
          { value: '__unassign', label: 'Elimină responsabil' },
          ...assignable.map((p) => ({ value: p.userId as string, label: p.fullName })),
        ]}
        className="w-48"
      />

      {/* Stateless trigger like the dropdowns above: the value stays empty and
          picking a day fires the bulk mutation immediately. */}
      <DateField
        value=""
        onChange={(v) => {
          if (!v) return;
          const [y, m, d] = v.split('-').map(Number);
          run('dueDate', toDueISO(new Date(y, m - 1, d)));
        }}
        placeholder="Setează termen"
        className="w-44"
      />

      <div className="flex items-center gap-1">
        <Select
          label="Adaugă etichetă"
          hideLabel
          size="sm"
          value={labelId}
          onChange={setLabelId}
          placeholder="Adaugă etichetă…"
          options={(labels ?? []).map((l) => ({ value: l.id, label: l.name, color: l.color }))}
          className="w-40"
        />
        <button
          type="button"
          disabled={!labelId}
          onClick={() => {
            run('addLabels', [labelId]);
            setLabelId('');
          }}
          className="rounded bg-gray-100 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          Adaugă
        </button>
      </div>

      <button
        type="button"
        onClick={async () => {
          if (await confirm({ message: `Ștergi ${selectedIds.length} sarcină(i)?`, danger: true }))
            run('delete');
        }}
        className="inline-flex items-center gap-1 rounded bg-red-50 px-2 py-1.5 text-sm text-red-700 hover:bg-red-100"
      >
        <Trash2 size={14} /> Șterge
      </button>

      <button
        type="button"
        onClick={onClear}
        className="ml-auto inline-flex items-center gap-1 rounded px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-100"
      >
        <X size={14} /> Golește
      </button>

      {bulk.isError && <p className="w-full text-xs text-red-600">Acțiunea în masă a eșuat.</p>}
    </div>
  );
}
